import MomentUtils from "@date-io/moment";
import Tooltip from "@material-ui/core/Tooltip";
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import moment from "moment";
import { FiCalendar, FiPlus, FiTrash2 } from "react-icons/fi";
import { handleKeyDown } from "../../../common/common";

export const parseOffHireDate = (value) => {
  if (moment.isMoment(value)) return value.clone();
  if (value instanceof Date) return moment(value);
  return moment(value, ["DD-MM-YYYY HH:mm", moment.ISO_8601], true);
};

export default function HireExpenseCalculator({
  isReadOnly = false,
  showOffHireAmounts = false,
  offHireDeductions = [],
  extraDetails = {},
  setExtraDetails = () => { },
  setOffHireDeductions = () => { },
  fuelDeductions = [],
  setFuelDeductions = () => { },
  miscDeductions = [],
  setMiscDeductions = () => { },
  totalDeductions = 0,
  totalMiscDeductions = 0,
  totalFuelDeductions = 0,
  totalOffHireDuration = 0,
  cveDeduction = 0,
  hirePerDayDeduction = 0,
  vesselData = {},
}) {
  const handleAddOffHire = () => {
    const newId = Math.max(...offHireDeductions?.map((item) => item.id), 0) + 1;
    const newItem = {
      id: newId,
      from: "",
      fromTz: 0,
      to: "",
      toTz: 0,
      duration: 0,
      offHireDeducation: 0,
      netDaysDedn: 0,
      remarks: "",
      grossHireReversal: 0,
      vesselAdcomReversal: 0,
      brokerageReversal: 0,
      amount: 0,
    };

    setOffHireDeductions([...offHireDeductions, newItem]);
  };

  const handleDeleteOffHire = (id) => {
    if (offHireDeductions?.length <= 1) return;
    const updated = offHireDeductions?.filter((item) => item.id !== id);
    setOffHireDeductions(updated);
  };

  const handleAddFuelDeduction = () => {
    const newId = Math.max(...fuelDeductions?.map((item) => item.id), 0) + 1;
    const newItem = {
      id: newId,
      sn: fuelDeductions?.length + 1,
      from: "",
      fromTz: 0,
      to: "",
      toTz: 0,
      fuelGrade: "",
      quantity: 0,
      price: 0,
      deductionPercentage: 0,
      amount: 0,
      remarks: "",
    };

    setFuelDeductions([...fuelDeductions, newItem]);
  };

  const handleDeleteFuelDeduction = (id) => {
    if (fuelDeductions?.length <= 1) return;
    const updated = fuelDeductions?.filter((item) => item.id !== id);
    const withUpdatedSn = updated?.map((item, index) => ({
      ...item,
      sn: index + 1,
    }));
    setFuelDeductions(withUpdatedSn);
  };

  const handleAddMiscDeduction = () => {
    const newId = Math.max(...miscDeductions?.map((item) => item.id), 0) + 1;
    const newItem = {
      id: newId,
      remarks: "",
      amount: 0,
    };
    setMiscDeductions([...miscDeductions, newItem]);
  };

  const handleDeleteMiscDeduction = (id) => {
    if (miscDeductions?.length <= 1) return;
    const updated = miscDeductions?.filter((item) => item.id !== id);
    setMiscDeductions(updated);
  };

  const handleOffHireChange = (id, field, value) => {
    const format = "DD-MM-YYYY HH:mm";
    const nextValue = (field === "from" || field === "to") && moment.isMoment(value)
      ? (value.isValid() ? value.format(format) : "")
      : value;

    const updated = offHireDeductions?.map((item) => {
      if (item.id !== id) return item;

      const updatedItem = {
        ...item,
        [field]:
          field === "remarks"
            ? value
            : field === "offHireDeducation"
              ? Math.min(100, Math.max(0, parseFloat(value) || 0))
            : field.includes("Tz")
              ? parseFloat(value) || 0
            : nextValue,
      };

      const { from, to, fromTz, toTz, offHireDeducation, hireRate } =
        updatedItem;
      const parsedFromTz = parseFloat(fromTz) || 0;
      const parsedToTz = parseFloat(toTz) || 0;
      const parsedDeduction = parseFloat(offHireDeducation) || 0;
      const parsedHireRate = parseFloat(hireRate) || 0;

      const parsedFrom = parseOffHireDate(from);
      const parsedTo = parseOffHireDate(to);
      const isValidFrom = parsedFrom.isValid();
      const isValidTo = parsedTo.isValid();

      if (isValidFrom && isValidTo) {
        const adjustedFrom = parsedFrom.subtract(
          parsedFromTz,
          "hours"
        );
        const adjustedTo = parsedTo.subtract(parsedToTz, "hours");

        const duration = Math.max(0, adjustedTo.diff(adjustedFrom, "days", true));
        updatedItem.duration = parseFloat(duration?.toFixed(6));

        if (!isNaN(parsedDeduction)) {
          updatedItem.netDaysDedn = parseFloat(
            ((duration * parsedDeduction) / 100)?.toFixed(6)
          );
        }

        if (!isNaN(parsedHireRate)) {
          const grossHireReversal = duration * parsedHireRate;
          const vesselAdcomReversal = grossHireReversal * 0.0375;
          const brokerageReversal = grossHireReversal * 0.0125;
          const amount =
            grossHireReversal - vesselAdcomReversal - brokerageReversal;

          updatedItem.grossHireReversal = grossHireReversal;
          updatedItem.vesselAdcomReversal = vesselAdcomReversal;
          updatedItem.brokerageReversal = brokerageReversal;
          updatedItem.amount = amount;
        }
      } else {
        updatedItem.duration = 0;
        updatedItem.netDaysDedn = 0;
        updatedItem.grossHireReversal = 0;
        updatedItem.vesselAdcomReversal = 0;
        updatedItem.brokerageReversal = 0;
        updatedItem.amount = 0;
      }

      return updatedItem;
    });

    setOffHireDeductions(updated);
  };

  const handleFuelChange = (id, field, value) => {
    const format = "DD-MM-YYYY HH:mm";

    const updated = fuelDeductions?.map((item) => {
      if (item.id !== id) return item;

      const updatedItem = { ...item };
      let newValue = value;

      if (field === "from" || field === "to") {
        const m = moment(value);
        if (m.isValid()) {
          newValue = m.format(format);
        } else {
          newValue = value;
        }
        updatedItem[field] = newValue;
      } else if (field === "remarks") {
        updatedItem.remarks = value;
      } else if (field === "fuelGrade") {
        updatedItem.fuelGrade = value;

        if (value === "Main") {
          updatedItem.price = parseFloat(vesselData?.bnkPriceDeliveryhs) || 0;
        } else if (value === "ECA") {
          updatedItem.price = parseFloat(vesselData?.bnkPriceDeliveryls) || 0;
        } else if (value === "MGO") {
          updatedItem.price = parseFloat(vesselData?.bnkPriceDeliverymgo) || 0;
        }
      } else {
        updatedItem[field] = parseFloat(value) || 0;
      }

      const quantity = parseFloat(updatedItem.quantity) || 0;
      const price = parseFloat(updatedItem.price) || 0;
      const deductionPercent = parseFloat(updatedItem.fuelDeducation) || 0;
      const commissionRate = deductionPercent / 100;

      updatedItem.amount = parseFloat(
        (quantity * price * commissionRate)?.toFixed(3)
      );

      return updatedItem;
    });

    setFuelDeductions(updated);
  };

  const handleMiscChange = (id, field, value) => {
    const updated = miscDeductions?.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          [field]: field === "remarks" ? value : parseFloat(value) || 0,
        };
      }
      return item;
    });
    setMiscDeductions(updated);
  };

  const formatCurrency = (val, decimals = 2) => {
    return Number(val || 0).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  return (
    <div className="flex flex-col gap-3 text-[11px] font-medium">
      {/* Off-Hire Deductions & Side Summary Card */}
      <div className="flex flex-col xl:flex-row gap-3 items-start w-full min-w-0">
        {/* Off-Hire Deductions Table */}
        <div className="flex-1 min-w-0 w-full overflow-hidden">
          <DeductionTable
            isReadOnly={isReadOnly}
            title="Off-Hire Deductions"
            minTableWidth={showOffHireAmounts ? 955 : undefined}
            columns={[
              { name: "S/N", width: "30px", align: "center" },
              { name: "From", width: showOffHireAmounts ? "130px" : "135px", align: "left" },
              { name: "Time Zone", width: showOffHireAmounts ? "60px" : "65px", align: "center" },
              { name: "To", width: showOffHireAmounts ? "130px" : "135px", align: "left" },
              { name: "Time Zone", width: showOffHireAmounts ? "60px" : "65px", align: "center" },
              { name: "Duration (Days)", width: showOffHireAmounts ? "80px" : "85px", align: "right" },
              { name: "% Deduction", width: "75px", align: "center" },
              { name: "Net Days Dedn", width: showOffHireAmounts ? "75px" : "80px", align: "right" },
              { name: "Remarks", width: showOffHireAmounts ? "90px" : "110px", align: "left" },
              ...(showOffHireAmounts ? [
                { name: "Hire Deduction", width: "95px", align: "right" },
                { name: "CVE Deduction", width: "95px", align: "right" },
              ] : []),
            ]}
            data={offHireDeductions}
            onAdd={handleAddOffHire}
            onDelete={handleDeleteOffHire}
            renderRow={(item) => (
              <>
                <td className="px-1.5 py-0 border-r border-b border-[#cbd5e1] align-middle text-center font-medium text-slate-700 bg-[#f0f4f9] text-[11px] h-[20px]">
                  <Tooltip title="Off-hire ID" placement="top" arrow>
                    <span>{item.id}</span>
                  </Tooltip>
                </td>

                <EditableCell
                  isDate
                  value={item.from}
                  onChange={(v) => handleOffHireChange(item.id, "from", v)}
                />

                <EditableCell
                  value={item.fromTz}
                  type="number"
                  onChange={(v) => handleOffHireChange(item.id, "fromTz", v)}
                />

                <EditableCell
                  isDate
                  value={item.to}
                  onChange={(v) => handleOffHireChange(item.id, "to", v)}
                />

                <EditableCell
                  value={item.toTz}
                  type="number"
                  onChange={(v) => handleOffHireChange(item.id, "toTz", v)}
                />

                <td className="px-1.5 py-0 border-r border-b border-[#cbd5e1] align-middle text-right font-medium text-slate-900 bg-[#f0f4f9] text-[11px] h-[20px] tabular-nums">
                  <Tooltip title={item.from && item.to && Number(item.duration) <= 0 ? "To must be after From after time-zone adjustment" : item?.duration?.toFixed(4)} placement="top" arrow>
                    <span className={item.from && item.to && Number(item.duration) <= 0 ? "text-rose-700" : undefined}>{item.from && item.to && Number(item.duration) <= 0 ? "Check dates" : item?.duration ? item.duration.toFixed(3) : "0.000"}</span>
                  </Tooltip>
                </td>

                <EditableCell
                  value={item.offHireDeducation}
                  type="number"
                  onChange={(v) => handleOffHireChange(item.id, "offHireDeducation", v)}
                />

                <td className="px-1.5 py-0 border-r border-b border-[#cbd5e1] align-middle text-right font-medium text-slate-900 bg-[#f0f4f9] text-[11px] h-[20px] tabular-nums">
                  <Tooltip title={item?.netDaysDedn?.toFixed(6)} placement="top" arrow>
                    <span>{item?.netDaysDedn ? item.netDaysDedn.toFixed(5) : "0.00000"}</span>
                  </Tooltip>
                </td>

                <EditableCell
                  value={item.remarks}
                  onChange={(v) => handleOffHireChange(item.id, "remarks", v)}
                />
                {showOffHireAmounts && <>
                  <td className="px-1.5 py-0 border-r border-b border-[#cbd5e1] align-middle text-right font-medium text-slate-900 bg-[#f0f4f9] text-[11px] h-[20px] tabular-nums">
                    {formatCurrency((Number(item.netDaysDedn) || 0) * (Number(vesselData?.hirePerDay) || 0), 6)}
                  </td>
                  <td className="px-1.5 py-0 border-r border-b border-[#cbd5e1] align-middle text-right font-medium text-slate-900 bg-[#f0f4f9] text-[11px] h-[20px] tabular-nums">
                    {formatCurrency((Number(item.netDaysDedn) || 0) * (Number(extraDetails?.cvePerDay) || 0), 6)}
                  </td>
                </>}
              </>
            )}
            footer={
              <>
                <td colSpan={5} className="px-2 py-0.5 text-right font-medium text-[11px] text-slate-700 border-r border-[#cbd5e1] bg-[#f0f4f9] capitalize">
                  Total Off-Hire Duration:
                </td>
                <td className="px-1.5 py-0.5 text-right align-middle bg-[#f0f4f9] border-r border-[#cbd5e1] font-medium text-[11px] text-slate-900 tabular-nums">
                  {totalOffHireDuration ? totalOffHireDuration.toFixed(4) : "0.0000"} Days
                </td>
                <td colSpan={3} className="px-2 py-0.5 align-middle bg-[#f0f4f9]"></td>
                {showOffHireAmounts && <>
                  <td className="px-1.5 py-0.5 text-right tabular-nums border-r border-[#cbd5e1] bg-[#f0f4f9]">{formatCurrency(hirePerDayDeduction, 6)}</td>
                  <td className="px-1.5 py-0.5 text-right tabular-nums border-r border-[#cbd5e1] bg-[#f0f4f9]">{formatCurrency(cveDeduction, 6)}</td>
                </>}
                {!isReadOnly && <td className="bg-[#f0f4f9]" />}
              </>
            }
          />
        </div>

        {/* Side Calculation Summary Card - Excel Sheet Table */}
        <div className={`w-full ${showOffHireAmounts ? "xl:w-[220px]" : "xl:w-[240px]"} flex-shrink-0`}>
          <div className="bg-white border border-[#cbd5e1] rounded-none overflow-hidden">
            <div className="bg-[#e2e8f0] px-2 py-1 border-b border-[#cbd5e1] font-medium text-[11px] text-[#1e293b] capitalize">
              Off-Hire Summary
            </div>
            <table className="w-full border-collapse text-[11px] font-medium">
              <tbody>
                <tr className="h-[20px] hover:bg-amber-50/50 transition-colors">
                  <td className="px-2 py-0.5 bg-[#f0f4f9] text-slate-700 font-medium border-r border-b border-[#cbd5e1] text-[11px] align-middle whitespace-nowrap capitalize">
                    Hire Deduction
                  </td>
                  <td className="px-1.5 py-0.5 bg-[#f0f4f9] text-slate-900 font-medium border-b border-[#cbd5e1] text-[11px] align-middle text-right tabular-nums">
                    {formatCurrency(hirePerDayDeduction, 3)}
                  </td>
                </tr>
                <tr className="h-[20px] hover:bg-amber-50/50 transition-colors">
                  <td className="px-2 py-0.5 bg-[#f0f4f9] text-slate-700 font-medium border-r border-b border-[#cbd5e1] text-[11px] align-middle whitespace-nowrap capitalize">
                    CVE Deduction
                  </td>
                  <td className="px-1.5 py-0.5 bg-[#f0f4f9] text-slate-900 font-medium border-b border-[#cbd5e1] text-[11px] align-middle text-right tabular-nums">
                    {formatCurrency(cveDeduction, 3)}
                  </td>
                </tr>
                <tr className="h-[20px] hover:bg-amber-50/50 transition-colors">
                  <td className="px-2 py-0.5 bg-[#f0f4f9] text-slate-700 font-medium border-r border-b border-[#cbd5e1] text-[11px] align-middle whitespace-nowrap capitalize">
                    Hire Per Day
                  </td>
                  <td className="px-1.5 py-0.5 bg-[#f0f4f9] text-slate-900 font-medium border-b border-[#cbd5e1] text-[11px] align-middle text-right tabular-nums">
                    {formatCurrency(vesselData?.hirePerDay, 3)}
                  </td>
                </tr>
                <tr className="h-[20px] hover:bg-amber-50/50 transition-colors">
                  <td className="px-2 py-0.5 bg-[#f0f4f9] text-slate-700 font-medium border-r border-[#cbd5e1] text-[11px] align-middle whitespace-nowrap capitalize">
                    CVE Per Day
                  </td>
                  <td className="p-0 bg-[#ffffd6] h-[20px] align-middle">
                    <input
                      type="number"
                      onKeyDown={handleKeyDown}
                      placeholder="0"
                      name="cvePerDay"
                      value={extraDetails?.cvePerDay ?? ""}
                      onChange={(e) => setExtraDetails({ ...extraDetails, cvePerDay: e.target.value })}
                      disabled={isReadOnly}
                      className="w-full h-full text-[11px] px-1.5 bg-[#ffffd6] focus:bg-white border-none outline-none font-medium text-slate-900 text-right tabular-nums focus:ring-1 focus:ring-inset focus:ring-amber-500 disabled:bg-[#f0f4f9]"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Fuel Deductions Table */}
      <div className="w-full">
        <DeductionTable
          isReadOnly={isReadOnly}
          title="Fuel Deductions"
          columns={[
            { name: "S/N", width: "135px", align: "center" },
            { name: "From", width: "135px", align: "left" },
            { name: "From TZ", width: "60px", align: "center" },
            { name: "To", width: "135px", align: "left" },
            { name: "To TZ", width: "60px", align: "center" },
            { name: "Fuel Grade", width: "80px", align: "left" },
            { name: "Quantity", width: "70px", align: "right" },
            { name: "Price", width: "75px", align: "right" },
            { name: "% Deduction", width: "90px", align: "center" },
            { name: "Amount", width: "85px", align: "right" },
            { name: "Remarks", width: "120px", align: "left" },
          ]}
          data={fuelDeductions}
          onAdd={handleAddFuelDeduction}
          onDelete={handleDeleteFuelDeduction}
          renderRow={(item) => (
            <>
              <td className="px-1.5 py-0 border-r border-b border-[#cbd5e1] align-middle text-center font-medium text-slate-700 bg-[#f0f4f9] text-[11px] h-[20px] whitespace-nowrap capitalize">
                Off-hire fuel dedn-{item.id}
              </td>

              <EditableCell
                isDate
                value={item.from}
                onChange={(v) => handleFuelChange(item.id, "from", v)}
              />

              <EditableCell
                value={item.fromTz}
                type="number"
                onChange={(v) => handleFuelChange(item.id, "fromTz", v)}
              />

              <EditableCell
                isDate
                value={item.to}
                onChange={(v) => handleFuelChange(item.id, "to", v)}
              />

              <EditableCell
                value={item.toTz}
                type="number"
                onChange={(v) => handleFuelChange(item.id, "toTz", v)}
              />

              <td className="p-0 border-r border-b border-[#cbd5e1] bg-[#ffffd6] h-[20px] align-middle">
                <select
                  value={item.fuelGrade || "Main"}
                  onChange={(e) => handleFuelChange(item.id, "fuelGrade", e.target.value)}
                  className="w-full h-full text-[11px] px-0.5 bg-transparent border-none outline-none font-medium text-slate-900 cursor-pointer capitalize"
                >
                  {["Main", "ECA", "MGO"].map((grade) => (
                    <option key={grade} value={grade} className="bg-white text-slate-900 capitalize">
                      {grade}
                    </option>
                  ))}
                </select>
              </td>

              <EditableCell
                value={item.quantity}
                type="number"
                onChange={(v) => handleFuelChange(item.id, "quantity", v)}
              />

              <td className="px-1.5 py-0 border-r border-b border-[#cbd5e1] align-middle text-right font-medium text-slate-800 bg-[#f0f4f9] text-[11px] h-[20px] tabular-nums">
                {formatCurrency(item?.price, 3)}
              </td>

              <EditableCell
                value={item.fuelDeducation}
                type="number"
                onChange={(v) => handleFuelChange(item.id, "fuelDeducation", v)}
              />

              <td className="px-1.5 py-0 border-r border-b border-[#cbd5e1] align-middle text-right font-medium text-slate-900 bg-[#f0f4f9] text-[11px] h-[20px] tabular-nums">
                {formatCurrency(item?.amount, 3)}
              </td>

              <EditableCell
                value={item.remarks}
                onChange={(v) => handleFuelChange(item.id, "remarks", v)}
              />
            </>
          )}
          footer={
            <>
              <td colSpan={9} className="px-2 py-0.5 text-right font-medium text-[11px] text-slate-700 border-r border-[#cbd5e1] bg-[#f0f4f9] capitalize">
                Total Fuel Deductions:
              </td>
              <td className="px-1.5 py-0.5 text-right align-middle bg-[#f0f4f9] border-r border-[#cbd5e1] font-medium text-[11px] text-slate-900 tabular-nums">
                {formatCurrency(totalFuelDeductions, 3)}
              </td>
              <td colSpan={!isReadOnly ? 2 : 1} className="px-2 py-0.5 align-middle bg-[#f0f4f9]"></td>
            </>
          }
        />
      </div>

      {/* Miscellaneous Deductions Table */}
      <div className="w-full">
        <DeductionTable
          isReadOnly={isReadOnly}
          title="Miscellaneous Deductions"
          columns={[
            { name: "S/N", width: "40px", align: "center" },
            { name: "Remarks / Detailed Narration", width: "70%", align: "left" },
            { name: "Amount", width: "30%", align: "right" },
          ]}
          data={miscDeductions}
          onAdd={handleAddMiscDeduction}
          onDelete={handleDeleteMiscDeduction}
          renderRow={(item) => (
            <>
              <td className="px-1.5 py-0 border-r border-b border-[#cbd5e1] align-middle text-center font-medium text-slate-700 bg-[#f0f4f9] text-[11px] h-[20px]">
                {item.id}
              </td>
              <EditableCell
                value={item.remarks}
                onChange={(v) => handleMiscChange(item.id, "remarks", v)}
                maxLength={256}
                placeholder="Enter detailed narration..."
              />
              <EditableCell
                value={item.amount}
                type="number"
                onChange={(v) => handleMiscChange(item.id, "amount", v)}
              />
            </>
          )}
          footer={
            <>
              <td colSpan={2} className="px-2 py-0.5 text-right font-medium text-[11px] text-slate-700 border-r border-[#cbd5e1] bg-[#f0f4f9] capitalize">
                Total Miscellaneous Deductions:
              </td>
              <td className="px-1.5 py-0.5 text-right align-middle bg-[#f0f4f9] border-r border-[#cbd5e1] font-medium text-[11px] text-slate-900 tabular-nums">
                {formatCurrency(totalMiscDeductions, 3)}
              </td>
              {!isReadOnly && <td className="px-2 py-0.5 align-middle bg-[#f0f4f9]"></td>}
            </>
          }
        />
      </div>

      {/* Grand Total Footer Bar */}
      <div className="flex items-center justify-between p-2 bg-[#f0f4f9] border border-[#cbd5e1] rounded-none mt-1">
        <div>
          {!isReadOnly && (
            <button
              type="button"
              onClick={handleAddMiscDeduction}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-none bg-[#313C4C] text-white hover:bg-[#1e293b] transition shadow-xs cursor-pointer capitalize"
            >
              <FiPlus className="w-3.5 h-3.5" />
              <span>Add Deduction Row</span>
            </button>
          )}
        </div>

        <div className="inline-flex items-center border border-[#cbd5e1] rounded-none overflow-hidden h-[24px]">
          <span className="bg-[#f0f4f9] text-[#1e293b] font-medium px-2 h-full flex items-center border-r border-[#cbd5e1] whitespace-nowrap text-[11px] capitalize">
            Total Deductions:
          </span>
          <div className="bg-[#ffffd6] px-3 h-full flex items-center text-[11px] font-medium text-slate-900 tabular-nums">
            {formatCurrency(totalDeductions, 3)}
          </div>
        </div>
      </div>
    </div>
  );
}

function DeductionTable({
  title,
  columns,
  minTableWidth,
  data = [],
  renderRow,
  footer,
  onAdd,
  onDelete,
  isReadOnly = false,
}) {
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-medium text-[11px] text-[#1e293b] capitalize m-0">
          {title}
        </h3>
        {!isReadOnly && onAdd && (
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 hover:text-slate-900 hover:underline cursor-pointer capitalize"
          >
            <FiPlus className="w-3 h-3" />
            <span>Add Row</span>
          </button>
        )}
      </div>

      <div className="bg-white border border-[#cbd5e1] rounded-none shadow-none overflow-hidden">
        <div className="overflow-x-auto scroll-thin">
          <table className="w-full min-w-full border-collapse text-[11px] font-medium" style={minTableWidth ? { minWidth: minTableWidth } : undefined}>
            <thead>
              <tr className="bg-[#e2e8f0] text-[#1e293b] h-[24px]">
                {columns.map((col) => (
                  <th
                    key={col.name}
                    style={{ width: col.width }}
                    className={`px-1.5 py-0.5 text-${col.align || "left"} font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize`}
                  >
                    {col.name}
                  </th>
                ))}
                {!isReadOnly && onDelete && (
                  <th className="px-1.5 py-0.5 text-center font-medium text-[11px] align-middle border-b border-[#cbd5e1] w-[35px] whitespace-nowrap capitalize">
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y-0">
              {data && data.length > 0 ? (
                data.map((item) => (
                  <tr key={item.id} className="h-[20px] hover:bg-amber-50/50 transition-colors">
                    {renderRow(item)}

                    {!isReadOnly && onDelete && (
                      <td className="p-0 align-middle text-center border-b border-[#cbd5e1] whitespace-nowrap w-[35px] h-[20px] bg-white">
                        <button
                          type="button"
                          onClick={() => onDelete(item.id)}
                          className="w-full h-full flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors border-none bg-transparent cursor-pointer p-0"
                          title="Delete row"
                        >
                          <FiTrash2 className="w-3 h-3" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length + (onDelete && !isReadOnly ? 1 : 0)} className="px-2 py-2 text-center text-slate-400 italic text-[11px] border-b border-[#cbd5e1]">
                    No deduction rows added.
                  </td>
                </tr>
              )}
            </tbody>
            {footer && (
              <tfoot className="bg-[#f8fafc] border-t border-[#cbd5e1]">
                <tr>{footer}</tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function EditableCell({
  value,
  type = "text",
  onChange,
  maxLength,
  isDate = false,
  placeholder = "",
}) {
  if (isDate) {
    return (
      <td className="p-0 border-r border-b border-[#cbd5e1] bg-[#ffffd6] h-[20px] align-middle min-w-[130px]">
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <div className="flex items-center w-full h-full px-0.5">
            <KeyboardDateTimePicker
              size="small"
              className="laytime-datepicker w-full bg-transparent h-[18px] text-[11px] text-slate-900 !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[18px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[18px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[18px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-0.5 [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-[18px] [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-slate-400 [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-0 [&_.MuiInputAdornment-root]:!h-[18px] [&_.MuiInputAdornment-root]:!p-0 [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[12px] [&_.MuiIconButton-root]:!w-[12px] [&_.MuiIconButton-root]:!min-w-[12px] [&_.MuiIconButton-root]:!min-h-[12px] [&_.MuiIconButton-root]:!bg-transparent [&_.MuiSvgIcon-root]:!w-2.5 [&_.MuiSvgIcon-root]:!h-2.5 [&_.MuiSvgIcon-root]:!text-[9px]"
              format="DD-MM-YYYY HH:mm"
              value={value ? parseOffHireDate(value) : null}
              onChange={(date) => onChange(date)}
              inputProps={{
                placeholder: "DD-MM-YYYY HH:mm",
                style: {
                  fontSize: "11px",
                  fontWeight: "normal",
                  padding: "0px",
                  height: "18px",
                  color: "#1e293b",
                  lineHeight: "18px",
                },
              }}
              InputProps={{
                disableUnderline: true,
                style: {
                  fontSize: "11px",
                  fontWeight: "normal",
                  paddingRight: "0px",
                  padding: "0px",
                  marginTop: "0px",
                  height: "18px",
                },
              }}
              keyboardIcon={
                <FiCalendar className="w-3 h-3 text-slate-500 hover:text-[#313C4C]" />
              }
              InputAdornmentProps={{
                position: "end",
                style: {
                  margin: 0,
                  padding: 0,
                },
              }}
              KeyboardButtonProps={{
                style: {
                  padding: 0,
                  margin: 0,
                  width: "12px",
                  height: "12px",
                  minWidth: "12px",
                },
                title: "Select date and time",
              }}
              disableMaskedInput={false}
              error={false}
              helperText={null}
              FormHelperTextProps={{
                style: { display: "none" },
              }}
              onError={() => { }}
            />
          </div>
        </MuiPickersUtilsProvider>
      </td>
    );
  }

  return (
    <td className="p-0 border-r border-b border-[#cbd5e1] bg-[#ffffd6] h-[20px] align-middle">
      <input
        type={type}
        value={value === 0 && type === "number" ? "" : value ?? ""}
        placeholder={placeholder || (type === "number" ? "0" : "")}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full h-full text-[11px] px-1 bg-[#ffffd6] focus:bg-white border-none outline-none font-medium text-slate-900 focus:ring-1 focus:ring-inset focus:ring-amber-500 capitalize ${type === "number" ? "text-right tabular-nums" : "text-left"}`}
      />
    </td>
  );
}
